<script setup lang="ts">
import type { PdpEcommerceBlockFitScaleProps } from './PdpEcommerceBlockFitScale.props'

/** Stable keys matching Algolia `fitting` (e.g. super_skinny) */
const CLOTHING_STEPS = ['slim', 'regular', 'over'] as const
const DENIM_STEPS = [
  'super_skinny',
  'skinny',
  'slim',
  'straight',
  'carrot',
  'relaxed',
  'loose',
] as const

const props = withDefaults(defineProps<PdpEcommerceBlockFitScaleProps>(), {
  fitting: null,
  isDenim: false,
  fitDescription: 'Taglio aderente che valorizza la figura.',
})

const { ts } = useI18n()

const steps = computed(() => (props.isDenim ? DENIM_STEPS : CLOTHING_STEPS))

const activeIndex = computed(() => {
  if (!props.fitting) return null
  const index = steps.value.findIndex(step => step === props.fitting)
  return index >= 0 ? index : null
})

const alignClass = computed(() => {
  if (activeIndex.value === 0) return 'justify-start'
  if (activeIndex.value === steps.value.length - 1) return 'justify-end'
  return 'justify-center'
})

const activeStep = computed(() =>
  activeIndex.value !== null ? steps.value[activeIndex.value] : null
)

const fitTitle = computed(() =>
  activeStep.value ? `${ts(`pdp.fitScale.fit.${activeStep.value}`)}` : null
)

const stepLabel = (step: string) => ts(`pdp.fitScale.fit.${step}`)
</script>

<template>
  <div v-if="activeIndex !== null" class="flex flex-col gap-6">
    <div class="flex flex-col gap-2.5">
      <div class="relative flex items-center justify-between gap-1">
        <div
          class="bg-primitives-black absolute inset-x-0 top-1/2 h-px -translate-y-1/2"
        />
        <div
          v-for="(step, index) in steps"
          :key="`dot-${step}`"
          class="relative z-10 flex h-2 w-fit items-center"
          :class="index === activeIndex ? alignClass : 'justify-center'"
        >
          <span
            class="text-light-8 invisible w-fit !text-[8px] uppercase"
            aria-hidden="true"
          >
            {{ stepLabel(step) }}
          </span>
          <div
            v-if="index === activeIndex"
            class="bg-primitives-black absolute size-2 rounded-full"
            :class="{
              'left-0': alignClass === 'justify-start',
              'right-0': alignClass === 'justify-end',
              'left-1/2 -translate-x-1/2': alignClass === 'justify-center',
            }"
          />
        </div>
      </div>
      <div class="flex justify-between gap-1">
        <span
          v-for="step in steps"
          :key="step"
          class="text-light-8 w-fit !text-[8px] uppercase"
        >
          {{ stepLabel(step) }}
        </span>
      </div>
    </div>

    <div class="gap-primitives-2 flex items-start">
      <div class="flex flex-col gap-2">
        <p class="flex items-center gap-2">
          <DefaultIconsSizes class="size-[17px] shrink-0" aria-hidden="true" />
          <span class="text-book-6 uppercase"
            >{{ fitTitle }}&nbsp;{{ $ts('pdp.fitScale.fitLabel') }}</span
          >
        </p>
        <p class="text-light-6">{{ fitDescription }}</p>
      </div>
    </div>
  </div>
</template>
